import React from "react";
import { Link } from "react-router-dom";
import PageShell from "../components/PageShell";
import CommunitySignupForm from "../components/CommunitySignupForm";
import { ArrowRight, Users, UserCheck, Sprout, HeartHandshake, HandHeart, MessageCircleHeart, Sparkles, BookOpen } from "lucide-react";

const groups = [
  { icon: UserCheck, title: "Women in business", desc: "Build your business with encouragement from women who understand the balance of faith, family and work." },
  { icon: Users, title: "Men in business", desc: "Lead your business with integrity and find other men who are growing with purpose." },
  { icon: Sprout, title: "Just starting out", desc: "Have an idea but no plan yet? Start here, ask your questions, and take your first steps with people beside you." },
];

const whatWeDo = [
  { n: 1, icon: HandHeart, title: "Meet and connect", desc: "Build real relationships with business owners who share your faith and open doors for each other." },
  { n: 2, icon: MessageCircleHeart, title: "Share and solve", desc: "Bring your questions about pricing, hiring or customers, and work through them with the group." },
  { n: 3, icon: Sparkles, title: "Learn how to grow", desc: "Practical help with social media, video, branding and websites, the areas Design My Story works in every day." },
  { n: 4, icon: BookOpen, title: "Pray and be encouraged", desc: "Keep God first in your business, and be lifted up by people who are praying for you." },
];

const moments = [
  { label: "Gatherings", sub: "Meeting in person", img: "https://images.unsplash.com/photo-1529390079861-591de354faf5?auto=format&fit=crop&w=900&q=80" },
  { label: "Learning together", sub: "Sharing what works", img: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=80" },
  { label: "Celebrating wins", sub: "Big and small", img: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=900&q=80" },
];

const steps = [
  { n: 1, title: "Tell us about you", desc: "Share your name, your business and what you hope to find here." },
  { n: 2, title: "We get to know you", desc: "We'll reach out to welcome you and answer your questions." },
  { n: 3, title: "Join the next gathering", desc: "We'll send you the details so you can meet the community." },
];

export default function Community() {
  return (
    <PageShell
      kicker="Design My Story Community"
      title="Grow your business God's way."
      subtitle="A community for women and men who want to build a business with faith, honesty and purpose, and who want to do it alongside people who understand."
    >
      {/* CTA buttons */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-4 pb-8 text-center">
        <div className="flex flex-wrap gap-3 justify-center">
          <a href="#signup" className="btn-peach" data-testid="cm-join-cta">Join the community <ArrowRight className="w-4 h-4" /></a>
          <a href="#about" className="btn-ghost-navy" data-testid="cm-learn-cta">Learn more <ArrowRight className="w-4 h-4" /></a>
        </div>
      </section>

      {/* Hero image band */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 pb-16">
        <div className="relative rounded-3xl overflow-hidden bg-[color:var(--dms-navy)]">
          <img
            src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1600&q=80"
            alt="Design My Story Community gathering"
            className="w-full h-[380px] md:h-[520px] object-cover opacity-85"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--dms-navy)]/90 via-[color:var(--dms-navy)]/30 to-transparent" />
          <div className="absolute left-0 right-0 bottom-0 p-6 md:p-10 text-white">
            <p className="kicker text-[color:var(--dms-peach)]">You don't have to build alone</p>
            <h2 className="font-serif italic text-2xl md:text-4xl mt-3 max-w-2xl leading-tight">Life is full of stories — Jesus is designing yours.</h2>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="max-w-5xl mx-auto px-5 lg:px-8 py-16 scroll-mt-24">
        <div className="text-center">
          <p className="kicker">Why we gather</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Business is better when it's carried together</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <p className="mt-6 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">Running a business can feel lonely, especially when you want your work to honor God. The Design My Story Community is a place to meet other business owners, share what's working, talk honestly about what isn't, and keep Jesus at the center of what you build.</p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {groups.map((g, i) => {
            const Icon = g.icon;
            return (
              <div key={i} className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-7 hover-lift" data-testid={`cm-group-${i}`}>
                <div className="w-11 h-11 rounded-full bg-[color:var(--dms-peach-light)]/60 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[color:var(--dms-navy)]" />
                </div>
                <h3 className="font-serif text-xl text-[color:var(--dms-navy)] mt-5">{g.title}</h3>
                <p className="mt-3 text-sm text-[color:var(--dms-navy)]/70">{g.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* What we do */}
      <section className="bg-[color:var(--dms-cream-2)]/50 py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center">
            <p className="kicker">Together</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">What we do together</h2>
            <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
            <p className="mt-6 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">We help each other grow, practically and spiritually.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whatWeDo.map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.n} className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6" data-testid={`cm-do-${w.n}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[color:var(--dms-peach)] flex items-center justify-center text-[color:var(--dms-navy)] font-serif text-sm font-semibold">{w.n}</div>
                    <Icon className="w-4 h-4 text-[color:var(--dms-navy)]/50" />
                  </div>
                  <h3 className="font-serif text-xl text-[color:var(--dms-navy)] mt-4">{w.title}</h3>
                  <p className="mt-2 text-sm text-[color:var(--dms-navy)]/65">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Moments */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-20">
        <div className="text-center">
          <p className="kicker">Moments</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Moments from our community</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <p className="mt-6 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">Faces, gatherings and stories from the people who are building together.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {moments.map((m, i) => (
            <div key={i} className="rounded-2xl overflow-hidden border border-[color:var(--dms-navy)]/10 bg-white hover-lift" data-testid={`cm-moment-${i}`}>
              <div className="aspect-[4/5] overflow-hidden bg-[color:var(--dms-cream-2)]">
                <img src={m.img} alt={m.label} className="w-full h-full object-cover" />
              </div>
              <div className="p-5">
                <p className="font-serif text-xl text-[color:var(--dms-navy)]">{m.label}</p>
                <p className="mt-1 text-sm text-[color:var(--dms-navy)]/65">{m.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Scripture quote */}
        <div className="mt-16 bg-[color:var(--dms-navy)] rounded-3xl p-10 md:p-14 text-center">
          <HeartHandshake className="w-7 h-7 text-[color:var(--dms-peach)] mx-auto" />
          <p className="font-serif italic text-2xl md:text-3xl text-white mt-6 leading-snug max-w-3xl mx-auto">
            "Commit your actions to the LORD, and your plans will succeed."
          </p>
          <p className="mt-5 kicker text-[color:var(--dms-peach)]" data-testid="cm-verse-ref">Proverbs 16:3 (NLT)</p>
        </div>
      </section>

      {/* How to join */}
      <section id="join" className="max-w-6xl mx-auto px-5 lg:px-8 pb-16 scroll-mt-24">
        <div className="text-center">
          <p className="kicker">How to join</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Join us in three simple steps</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-7" data-testid={`cm-step-${s.n}`}>
              <div className="w-10 h-10 rounded-full bg-[color:var(--dms-peach)] flex items-center justify-center text-[color:var(--dms-navy)] font-serif text-lg font-semibold">{s.n}</div>
              <h3 className="font-serif text-xl text-[color:var(--dms-navy)] mt-4">{s.title}</h3>
              <p className="mt-2 text-sm text-[color:var(--dms-navy)]/65">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Signup form */}
      <section id="signup" className="scroll-mt-24 pb-20">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <CommunitySignupForm />
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[color:var(--dms-navy)] py-20">
        <div className="max-w-3xl mx-auto text-center px-5">
          <p className="kicker text-[color:var(--dms-peach)]">Your story is still being written</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mt-4">Join the Design My Story Community and grow your business God's way.</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="#signup" className="btn-peach" data-testid="cm-final-join">Join the community <ArrowRight className="w-4 h-4" /></a>
            <a href="https://instagram.com/design.my.story" target="_blank" rel="noreferrer" className="btn-outline-light" data-testid="cm-final-instagram">@design.my.story <ArrowRight className="w-4 h-4" /></a>
          </div>
          <p className="mt-6 text-white/60 text-sm">We reply to every message within one business day.</p>
        </div>
      </section>
    </PageShell>
  );
}
